// Export V6 — one timeline frame drawn exactly like the editor preview (VideoPlayer.draw):
// background → active video clip (fit, shape, filters, transitions) → images → elements → captions.
// Drawing is synchronous; the caller supplies already-decoded frames for the clips it asked for.
import { drawFrame, drawCaptionFrame, hasAnyCaptionObjects } from "@/lib/animator";
import { computeImageEffect } from "@/lib/imageEffects";
import { drawElement } from "@/lib/elements";
import { resolveActiveVideoClip, resolveActiveImages, resolveActiveElements, sourceTimeFor, clipSourceUrl, absUrl, cssFilter, prevVideoClip, clipCrop, cropRect, clipOpacity } from "@/lib/timeline";
import { traceShape, isShaped, backgroundObject } from "@/lib/shapes";
import { cameraTf, applyCamera } from "@/lib/motion";

// Anime / Cartoon style (server: video_effects.py) approximated with canvas filters.
function clipFilter(o) {
  const base = cssFilter(o.filters);
  const k = Math.max(0, Math.min(1, (o.effectIntensity != null ? o.effectIntensity : 70) / 100));
  let fx = "";
  if (o.effect === "anime") fx = `saturate(${(1 + 0.7 * k).toFixed(3)}) contrast(${(1 + 0.2 * k).toFixed(3)}) brightness(${(1 + 0.02 * k).toFixed(3)})`;
  else if (o.effect === "cartoon") fx = `saturate(${(1 + 0.6 * k).toFixed(3)}) contrast(${(1 + 0.45 * k).toFixed(3)})`;
  if (!fx) return base;
  return base === "none" ? fx : `${base} ${fx}`;
}

const dims = (d) => [d?.videoWidth || d?.displayWidth || d?.naturalWidth || d?.width || 0, d?.videoHeight || d?.displayHeight || d?.naturalHeight || d?.height || 0];

// What the frame at time t needs decoded: { clip, prev, bg } each { key, url, srcT }.
export function frameNeeds(s, t, baseClip) {
  const out = {};
  const bg = backgroundObject(s.mediaTracks);
  if (bg && bg.bgType === "video" && (bg.asset_url || bg.assetUrl)) {
    const off = bg.source_start || 0;
    const len = Math.max(0.1, (s.bgDurations?.get(bg) || 1e9) - off);
    out.bg = { key: bg, url: absUrl(bg.asset_url || bg.assetUrl), srcT: bg.loop === false ? off + t : off + (t % len) };
  }
  let active = resolveActiveVideoClip(s.mediaTracks, t);
  const anyVideo = (s.mediaTracks || []).some((tr) => tr.type === "video" && (tr.objects || []).length);
  if (!active && !anyVideo && s.videoUrl && t < s.duration) active = { tr: { type: "video" }, o: baseClip };
  if (active) {
    out.clip = { key: active.o, o: active.o, url: clipSourceUrl(active.o, s.videoUrl), srcT: sourceTimeFor(active.o, t) };
    const tr = active.o.transition;
    if (tr?.type === "crossfade" && (tr.duration || 0) > 0 && t < (active.o.start || 0) + tr.duration) {
      const prev = prevVideoClip(s.mediaTracks, active.o);
      if (prev) out.prev = { key: prev.o, url: clipSourceUrl(prev.o, s.videoUrl), srcT: sourceTimeFor(prev.o, Math.max(0, (prev.o.end || 0) - 0.06)) };
    }
  }
  return out;
}

function drawFit(ctx, el, cw, ch, fitMode, letterbox, crop = null) {
  const [fw, fh] = dims(el);
  if (!fw || !fh) return;
  const [sx, sy, vw, vh] = cropRect(crop, fw, fh);
  if (fitMode === "contain") {
    if (letterbox === "blur") {
      const bs = Math.max(cw / vw, ch / vh);
      ctx.save(); ctx.filter = "blur(24px)"; ctx.drawImage(el, sx, sy, vw, vh, (cw - vw * bs) / 2, (ch - vh * bs) / 2, vw * bs, vh * bs); ctx.restore();
    } else if (letterbox !== "none") { ctx.fillStyle = "#000"; ctx.fillRect(0, 0, cw, ch); }
    const sc = Math.min(cw / vw, ch / vh);
    ctx.drawImage(el, sx, sy, vw, vh, (cw - vw * sc) / 2, (ch - vh * sc) / 2, vw * sc, vh * sc);
    return;
  }
  const sc = Math.max(cw / vw, ch / vh);
  ctx.drawImage(el, sx, sy, vw, vh, (cw - vw * sc) / 2, (ch - vh * sc) / 2, vw * sc, vh * sc);
}

function drawBackground(ctx, cw, ch, s, bgFrame) {
  const bg = backgroundObject(s.mediaTracks);
  if (!bg) return;
  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, bg.opacity != null ? bg.opacity : 1));
  const bt = bg.bgType || "solid";
  if (bt === "gradient") {
    const ang = ((bg.angle != null ? bg.angle : 90) * Math.PI) / 180;
    const dx = Math.cos(ang), dy = Math.sin(ang);
    const g = ctx.createLinearGradient(cw / 2 - (dx * cw) / 2, ch / 2 - (dy * ch) / 2, cw / 2 + (dx * cw) / 2, ch / 2 + (dy * ch) / 2);
    g.addColorStop(0, bg.from || "#111827"); g.addColorStop(1, bg.to || "#000000");
    ctx.fillStyle = g; ctx.fillRect(0, 0, cw, ch);
  } else if (bt === "image" && (bg.asset_url || bg.assetUrl)) {
    const img = s.images.get(absUrl(bg.asset_url || bg.assetUrl));
    if (img?.naturalWidth) {
      const fit = bg.fit || "cover";
      const r = fit === "contain" ? Math.min(cw / img.naturalWidth, ch / img.naturalHeight) : Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
      if (fit === "contain") { ctx.fillStyle = "#000"; ctx.fillRect(0, 0, cw, ch); }
      ctx.drawImage(img, (cw - img.naturalWidth * r) / 2, (ch - img.naturalHeight * r) / 2, img.naturalWidth * r, img.naturalHeight * r);
    }
  } else if (bt === "video") {
    if (bgFrame) drawFit(ctx, bgFrame, cw, ch, "cover");
  } else { ctx.fillStyle = bg.color || "#000000"; ctx.fillRect(0, 0, cw, ch); }
  ctx.restore();
}

function drawShapedVideo(ctx, el, cw, ch, o, alpha) {
  const [vw, vh] = dims(el);
  if (!vw || !vh) return;
  const bw = cw * Math.max(0.02, Math.min(1, o.shapeW != null ? o.shapeW : 1));
  const bh = ch * Math.max(0.02, Math.min(1, o.shapeH != null ? o.shapeH : 1));
  const ms = Math.max(0.1, o.mScale != null ? o.mScale : 1);
  const rot = ((o.rotation || 0) * Math.PI) / 180;
  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
  ctx.filter = clipFilter(o);
  ctx.translate((o.shapeX != null ? o.shapeX : 0.5) * cw, (o.shapeY != null ? o.shapeY : 0.5) * ch);
  if (rot) ctx.rotate(rot);
  traceShape(ctx, bw, bh, o.shape || "rect", o.cornerRadius || 0.12);
  ctx.clip();
  const r = ((o.mFit || "cover") === "contain" ? Math.min(bw / vw, bh / vh) : Math.max(bw / vw, bh / vh)) * ms;
  ctx.drawImage(el, -(vw * r) / 2 - (o.mX || 0) * bw, -(vh * r) / 2 - (o.mY || 0) * bh, vw * r, vh * r);
  ctx.restore();
  ctx.filter = "none";
}

function drawImages(ctx, t, cw, ch, s) {
  resolveActiveImages(s.mediaTracks, t).forEach(({ o }) => {
    const img = s.images.get(absUrl(o.asset_url));
    if (!img?.naturalWidth) return;
    const fx = computeImageEffect(o, t);
    const boxW = cw * Math.max(0.02, Math.min(1, o.scale != null ? o.scale : 0.5)) * fx.scale;
    const shape = o.shape || "rect", fit = o.fit || "contain", cr = o.cornerRadius || 0;
    const needsPrep = shape !== "rect" || fit === "cover" || fit === "fill" || fit === "stretch" || cr > 0;
    let iw = boxW, ih;
    if (!needsPrep) ih = iw * (img.naturalHeight / img.naturalWidth);
    else if (shape === "circle" || shape === "square") ih = boxW;
    else if (shape === "oval") ih = boxW * 0.7;
    else ih = o.boxH ? ch * o.boxH : boxW * 0.75;
    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, (o.opacity != null ? o.opacity : 1) * fx.alpha));
    ctx.filter = fx.blur > 0.1 ? `${cssFilter(o.filters)} blur(${fx.blur.toFixed(1)}px)`.replace("none ", "").trim() : cssFilter(o.filters);
    ctx.translate(((o.x != null ? o.x : 0.5) + fx.dx) * cw, ((o.y != null ? o.y : 0.5) + fx.dy) * ch);
    if (o.rotation) ctx.rotate((o.rotation * Math.PI) / 180);
    if (needsPrep) {
      ctx.beginPath();
      if (shape === "circle" || shape === "oval") ctx.ellipse(0, 0, iw / 2, ih / 2, 0, 0, Math.PI * 2);
      else { const rad = (shape === "rounded" || cr > 0) ? Math.min(iw, ih) * Math.min(0.5, cr || 0.12) : 0; if (ctx.roundRect) ctx.roundRect(-iw / 2, -ih / 2, iw, ih, rad); else ctx.rect(-iw / 2, -ih / 2, iw, ih); }
      ctx.clip();
      if (fit === "fill" || fit === "stretch") ctx.drawImage(img, -iw / 2, -ih / 2, iw, ih);
      else {
        const r = fit === "cover" ? Math.max(iw / img.naturalWidth, ih / img.naturalHeight) : Math.min(iw / img.naturalWidth, ih / img.naturalHeight);
        ctx.drawImage(img, -(img.naturalWidth * r) / 2, -(img.naturalHeight * r) / 2, img.naturalWidth * r, img.naturalHeight * r);
      }
    } else ctx.drawImage(img, -iw / 2, -ih / 2, iw, ih);
    ctx.restore();
    ctx.filter = "none";
  });
}

// frames: { clip, prev, bg } decoded drawables (VideoFrame or <video>) for frameNeeds(t).
export function drawTimelineFrame(ctx, t, cw, ch, s, need, frames) {
  ctx.fillStyle = "#000"; ctx.fillRect(0, 0, cw, ch);
  drawBackground(ctx, cw, ch, s, frames.bg);
  if (need.clip && frames.clip) {
    const o = need.clip.o, tr = o.transition;
    let alpha = 1, tx = 0, sc = 1;
    if (tr && tr.type && tr.type !== "none" && (tr.duration || 0) > 0 && t < (o.start || 0) + tr.duration) {
      const p = Math.min(1, Math.max(0, (t - (o.start || 0)) / tr.duration));
      if (tr.type === "fade") alpha = p;
      else if (tr.type === "slide") tx = (1 - p) * cw * (tr.direction === "left" ? -1 : 1);
      else if (tr.type === "zoom") { sc = 1.2 - 0.2 * p; alpha = p < 0.2 ? p / 0.2 : 1; }
      else if (tr.type === "crossfade") { if (frames.prev) drawFit(ctx, frames.prev, cw, ch, s.fitMode, s.letterbox); alpha = p; }
    }
    alpha *= clipOpacity(o);
    if (isShaped(o)) drawShapedVideo(ctx, frames.clip, cw, ch, o, alpha);
    else {
      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
      ctx.filter = clipFilter(o);
      if (tx !== 0 || sc !== 1) { ctx.translate(cw / 2 + tx, ch / 2); ctx.scale(sc, sc); ctx.translate(-cw / 2, -ch / 2); }
      { const [fvw, fvh] = dims(frames.clip); applyCamera(ctx, cameraTf(o, t), cw, ch, { vw: fvw, vh: fvh, mode: s.fitMode, crop: clipCrop(o) }); }
      // Faded clips show the background through, so skip the black letterbox fill.
      drawFit(ctx, frames.clip, cw, ch, s.fitMode, alpha < 1 && s.letterbox !== "blur" ? "none" : s.letterbox, clipCrop(o));
      ctx.restore();
      ctx.filter = "none";
    }
  }
  drawImages(ctx, t, cw, ch, s);
  resolveActiveElements(s.mediaTracks, t).forEach(({ o }) => drawElement(ctx, o, cw, ch));
  if (hasAnyCaptionObjects(s.layers)) drawCaptionFrame(ctx, t, cw, ch, s.layers, s.config, s.segments, { noClear: true });
  else if (s.segments?.length) drawFrame(ctx, t, cw, ch, s.segments, s.config, { noClear: true });
}
